import { useEffect, useState } from "react";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, Cell,
  PieChart, Pie, RadialBarChart, RadialBar,
} from "recharts";
import { api } from "../api";
import { PageHeader, GlassCard, Spinner } from "../components/ui";

const COLORS = ["#3B82F6", "#8B5CF6", "#06b6d4", "#a78bfa", "#60a5fa", "#c084fc"];
const tooltipStyle = { background: "#12121a", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 12, color: "#fff", fontSize: 12 };

export default function Analytics() {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/analytics").then(({ data }) => setData(data)).catch(() => setData(false));
  }, []);

  if (data === null) return <Spinner />;
  if (data === false) return <div className="text-slate-400">Could not load analytics.</div>;

  const factorData = data.funding_factors.map((f) => ({ name: f.label, value: f.value }));

  return (
    <div className="space-y-6">
      <PageHeader label="Insights" title="Analytics"
        subtitle="Visualize your progress across foundation, credit, vendors, and funding readiness." />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          { l: "Funding Score", v: data.funding_score },
          { l: "Documents", v: data.documents },
          { l: "Foundation Groups", v: data.foundation_by_group.length },
          { l: "Vendor Records", v: data.vendor_status.reduce((a, b) => a + b.value, 0) },
        ].map((s) => (
          <GlassCard key={s.l} className="p-5">
            <div className="cc-heading text-3xl font-700 text-white">{s.v}</div>
            <div className="text-sm text-slate-400 mt-1">{s.l}</div>
          </GlassCard>
        ))}
      </div>

      <div className="grid lg:grid-cols-2 gap-5">
        <GlassCard className="p-6">
          <h3 className="cc-heading text-lg font-500 text-white mb-5">Foundation Progress by Area</h3>
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={data.foundation_by_group}>
              <XAxis dataKey="group" tick={{ fill: "#64748b", fontSize: 11 }} axisLine={false} tickLine={false} />
              <YAxis domain={[0, 100]} tick={{ fill: "#64748b", fontSize: 11 }} axisLine={false} tickLine={false} />
              <Tooltip contentStyle={tooltipStyle} cursor={{ fill: "rgba(255,255,255,0.03)" }} />
              <Bar dataKey="value" radius={[6, 6, 0, 0]}>
                {data.foundation_by_group.map((d, i) => <Cell key={d.group || i} fill={COLORS[i % COLORS.length]} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </GlassCard>

        <GlassCard className="p-6">
          <h3 className="cc-heading text-lg font-500 text-white mb-5">Funding Readiness Factors</h3>
          <ResponsiveContainer width="100%" height={280}>
            <BarChart layout="vertical" data={factorData} margin={{ left: 30 }}>
              <XAxis type="number" domain={[0, 100]} hide />
              <YAxis type="category" dataKey="name" tick={{ fill: "#94a3b8", fontSize: 10 }} axisLine={false} tickLine={false} width={110} />
              <Tooltip contentStyle={tooltipStyle} cursor={{ fill: "rgba(255,255,255,0.03)" }} />
              <Bar dataKey="value" radius={[0, 6, 6, 0]} fill="#8B5CF6">
                {factorData.map((d, i) => <Cell key={d.name || i} fill={COLORS[i % COLORS.length]} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </GlassCard>

        <GlassCard className="p-6">
          <h3 className="cc-heading text-lg font-500 text-white mb-5">Vendor Status</h3>
          {data.vendor_status.length === 0 ? (
            <p className="text-sm text-slate-500 py-20 text-center">No vendor data yet.</p>
          ) : (
            <ResponsiveContainer width="100%" height={280}>
              <PieChart>
                <Pie data={data.vendor_status} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={55} outerRadius={95} paddingAngle={3}>
                  {data.vendor_status.map((d, i) => <Cell key={d.name || i} fill={COLORS[i % COLORS.length]} stroke="none" />)}
                </Pie>
                <Tooltip contentStyle={tooltipStyle} />
              </PieChart>
            </ResponsiveContainer>
          )}
          <div className="flex flex-wrap gap-3 justify-center mt-2">
            {data.vendor_status.map((v, i) => (
              <span key={v.name} className="flex items-center gap-1.5 text-xs text-slate-400 capitalize">
                <span className="h-2.5 w-2.5 rounded-full" style={{ background: COLORS[i % COLORS.length] }} /> {v.name} ({v.value})
              </span>
            ))}
          </div>
        </GlassCard>

        <GlassCard className="p-6">
          <h3 className="cc-heading text-lg font-500 text-white mb-5">Overall Funding Readiness</h3>
          <ResponsiveContainer width="100%" height={280}>
            <RadialBarChart innerRadius="70%" outerRadius="100%" data={[{ name: "Funding", value: data.funding_score, fill: "#3B82F6" }]} startAngle={90} endAngle={-270}>
              <RadialBar minAngle={15} background={{ fill: "rgba(255,255,255,0.05)" }} dataKey="value" cornerRadius={20} />
              <text x="50%" y="50%" textAnchor="middle" dominantBaseline="middle" fill="#fff" fontSize="34" fontWeight="700" fontFamily="Outfit">{data.funding_score}</text>
            </RadialBarChart>
          </ResponsiveContainer>
        </GlassCard>
      </div>
    </div>
  );
}
